import React, { useState } from "react";
import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import { Overlay } from "@/shared/globals";
import { Filter as FilterIcon } from "@/assets/Icons/Filter";
import { Delete } from "@/assets/Icons/Delete";
import { LeftArrow } from "@/assets/Icons/LeftArrow";
import { useEscapeAndStopScrollingEffect } from "@/hooks/useEscapeAndStopScrollingEffect";
import {
  CloseIconDesktop,
  CloseIconMobile,
  Container,
  FilterSection,
  FlipSvg,
  ScrollableSection,
  StyledFlexRow,
  StyledH3WithMargin,
} from "./styled";
import { LongTailRightArrow } from "@/assets/Icons/LongTailRightArrow";
import { useStore } from "@/lib/storeData/StoreContext";
import {
  ProductAttributeValue,
  ProductCollection,
  ProductOptionValue,
  useFullTextSearchAndFilterQuery,
  useGetFiltersQuery,
} from "@/generated/graphql";
import { getAvailableFilters, getFiltersSelectors } from "./helperFunctions";
import { ApplyFilterSection } from "./components/ApplyFilterSection";
import { AvailableFilterChoices } from "./components/AvailableFilterChoices";
import { FiltersSection } from "./components/FiltersSection";
import { FilterByPrice } from "./components/FilterByPrice";
import { DefaultFilters } from "./consts";
import { useSearchParams } from "@/i18n/i18n-navigation";
import { parseSearchParams } from "@/shared/utils/search-params";

interface SelectedFiltersProps {
  id: string | null | undefined;
  name: string | null | undefined;
}

interface FilterProps {
  /** when we have a collectionPageId, so we are on a collection page and that is its id */
  collectionPageId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const Filter = ({ collectionPageId, isOpen, onClose }: FilterProps) => {
  const intl = useIntl();
  const { storeId } = useStore();
  const searchParams = useSearchParams();
  const queries = parseSearchParams(searchParams);
  const [selectedFilter, setSelectedFilter] = useState<SelectedFiltersProps>({
    id: "",
    name: "",
  });

  // no way to have a collectionPageId with other filters
  const selectedFiltersIds = collectionPageId
    ? [collectionPageId]
    : typeof queries.filters === "string"
      ? [queries.filters]
      : queries.filters || [];

  const { attributeValueSelectors, collectionIds, optionValueSelectors } =
    getFiltersSelectors(selectedFiltersIds || []);
  const { data, error, loading } = useGetFiltersQuery({
    variables: {
      storeId,
    },
  });

  const { data: products } = useFullTextSearchAndFilterQuery({
    variables: {
      storeId,
      connectionSettings: {
        offset: 0,
        first: 24,
      },
      filters: {
        search: queries.query,
        collectionIds,
        attributeValueSelectors,
        optionValueSelectors,
        priceRange: {
          min: parseFloat(queries.minPrice) || undefined,
          max: parseFloat(queries.maxPrice) || undefined,
        },
      },
    },
  });

  useEscapeAndStopScrollingEffect({ isOpen: isOpen!, onClose });

  const { filtersDictionary } = getAvailableFilters({
    attributesProductsDistribution: data?.getApplicableFilters
      ?.attributeValues! as ProductAttributeValue[],
    collectionsProductsDistribution: data?.getApplicableFilters
      ?.collections as ProductCollection[],
    optionsProductsDistribution: data?.getApplicableFilters
      ?.optionValues as ProductOptionValue[],
    intl,
  });

  return (
    <>
      {isOpen && <Overlay onClick={onClose} />}
      <Container isOpen={isOpen!}>
        <FilterSection>
          <StyledFlexRow>
            {selectedFilter.name === "" ? (
              <FilterIcon />
            ) : (
              <FlipSvg
                onClick={() =>
                  setSelectedFilter({
                    id: "",
                    name: "",
                  })
                }
              >
                <LongTailRightArrow />
              </FlipSvg>
            )}
            <StyledH3WithMargin>
              {selectedFilter.name === "" ? (
                <FormattedMessage defaultMessage="Filter" id="9Obw6C" />
              ) : selectedFilter.name === DefaultFilters.Collections ? (
                <FormattedMessage
                  defaultMessage="Product Collections"
                  id="oV52BO"
                />
              ) : selectedFilter.name === DefaultFilters.Price ? (
                <FormattedMessage defaultMessage="Price" id="b1zuN9" />
              ) : (
                selectedFilter.name
              )}
            </StyledH3WithMargin>
          </StyledFlexRow>
          <CloseIconDesktop onClick={onClose}>
            <Delete />
          </CloseIconDesktop>
          <CloseIconMobile onClick={onClose}>
            <LeftArrow rotateOnRTL />
          </CloseIconMobile>
        </FilterSection>
        {selectedFilter.name === "" && (
          <FiltersSection
            selectFilter={setSelectedFilter}
            availableFilters={filtersDictionary}
            loading={loading}
            selectedFiltersIds={selectedFiltersIds}
          />
        )}
        {selectedFilter.id && (
          <ScrollableSection>
            <AvailableFilterChoices
              availableFilters={filtersDictionary[selectedFilter.id]}
              loading={loading}
              error={error!}
              selectedFilterIds={selectedFiltersIds}
            />
          </ScrollableSection>
        )}
        {selectedFilter.name === "Price" && <FilterByPrice />}
        <ApplyFilterSection
          onClose={onClose!}
          totalCount={products?.fullTextSearchAndFilter?.totalCount || 0}
          loading={loading}
        />
      </Container>
    </>
  );
};

export default Filter;
